import React, { useEffect, useState } from "react";
import { Menu, Steps, Row, Tooltip } from "antd";
import {
  DownOutlined,
  DownSquareOutlined,
  UpOutlined,
  UpSquareOutlined,
} from "@ant-design/icons";
import "./SidebarQuestionnaire.scss";
import QuestionBadges from "./QuestionBadges";
import GoalIcon from "./GoalIcon";
import Loader from "../Common/Loader";

const { Step } = Steps;

export default function SidebarQuestionnaire({
  defaultSelectedKeys,
  domains = [],
  goalsData = [],
  goalsStatusData = [],
  selectedDomainIndex,
  selectedGoalID,
  handleGoalClick,
  handleDomainClick,
  handleQuestionClick,
  isDisabled,
  isActiveSurvey,
  assessmentName,
  onSidebarSelect,
  isAdminLogin,
}) {
  const [isAssessmentOpen, setIsAssessmentOpen] = useState(true);
  const [openDomainId, setOpenDomainId] = useState(null);
  const toggleDomain = (id) => {
    setOpenDomainId((prevId) => (prevId === id ? null : id));
  };

  const returnGoalStyle = (id) => {
    return id === selectedGoalID ? { color: "#fff" } : {};
  };

  const toggleAssessment = () => {
    onSidebarSelect();
    setIsAssessmentOpen((prev) => !prev);
    // If it's opening, set default domain and goal
    if (!isAssessmentOpen && domains.length > 0) {
      const firstDomain = domains[0];
      const domainId = firstDomain.id;
      setOpenDomainId(domainId);
      handleDomainClick(firstDomain);

      const goalsInFirstDomain =
        goalsData?.length > 0 &&
        goalsData.filter(
          (goal) =>
            goal.domain_id === domainId || goal?.category_id === domainId
        );

      if (goalsInFirstDomain.length > 0) {
        handleGoalClick(goalsInFirstDomain[0]);
      }
    }
  };

  useEffect(() => {
    const el = document.getElementById(`goal-${selectedGoalID}`);
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedGoalID]);

  useEffect(() => {
    if (!isActiveSurvey) {
      setOpenDomainId(null); // Collapse the open domain
      setIsAssessmentOpen(false); // Optionally collapse entire CRR section
    } else setIsAssessmentOpen(true);
  }, [isActiveSurvey]);

  useEffect(() => {
    // open the selected domain
    if (domains.length > 0) {
      setOpenDomainId(selectedDomainIndex);
    }
  }, [isActiveSurvey, selectedDomainIndex]);

  return (
    <div
      className={`crr-survey SidebarQuestionnaire ${
        isDisabled ? "disabled" : ""
      }`}
    >
      {isDisabled ? (
        <Tooltip
          title="Complete CSF 2.0 assessment to unlock."
          overlayInnerStyle={{ fontSize: "18px" }}
        >
          <div className={`sidebar-domain-wrapper assessment-name`}>
            {assessmentName}
          </div>
        </Tooltip>
      ) : (
        <div
          className={`sidebar-domain-wrapper assessment-name ${
            isActiveSurvey ? "active-assessment" : ""
          }`}
          onClick={toggleAssessment}
        >
          {assessmentName}
          {isAssessmentOpen ? <UpSquareOutlined /> : <DownSquareOutlined />}
        </div>
      )}

      {isAssessmentOpen && !isDisabled && (
        <Menu
          style={{ backgroundColor: "var(--app-chrome-color)" }}
          selectable={false}
          theme="dark"
          defaultSelectedKeys={
            defaultSelectedKeys ? [defaultSelectedKeys] : [""]
          }
          mode="inline"
        >
          {domains.map((domain) => (
            <Menu.Item className="sideboard-menu-item" key={domain?.id}>
              <div
                id={`domain-${domain?.id}`}
                className={
                  selectedDomainIndex === domain?.id
                    ? "sidebar-domain-wrapper active-domain"
                    : "sidebar-domain-wrapper"
                }
                title={domain?.domain}
                onClick={() => {
                  toggleDomain(domain?.id);
                  handleDomainClick(domain);
                }}
                style={{ paddingRight: "10px" }}
              >
                <div className="domain-name-wrapper">
                  <div className="domain-no">
                    {domain?.order
                      ? domain.order < 10
                        ? "0" + domain.order
                        : domain.order
                      : domain?.id < 10
                      ? "0" + domain.id
                      : domain.id}
                  </div>
                  <div className="domain-Name">
                    {domain?.domain || domain?.name}
                  </div>
                </div>
                {openDomainId === domain?.id ? (
                  <UpOutlined style={{ fontSize: 15 }} />
                ) : (
                  <DownOutlined style={{ fontSize: 15 }} />
                )}
              </div>

              {openDomainId === domain?.id &&
                (goalsData.length > 0 ? (
                  <Steps
                    current={selectedGoalID}
                    direction="vertical"
                    style={{ paddingLeft: "36px", paddingRight: "25px" }}
                  >
                    {goalsData
                      ?.filter((goal) => goal?.id)
                      .map((goal) => {
                        const {
                          id,
                          name,
                          is_goal,
                          order,
                          mil_no,
                          section_status,
                        } = goal;
                        return (
                          <Step
                            id={`goal-${id}`}
                            className="steps"
                            key={id}
                            onClick={() => handleGoalClick(goal)}
                            icon={
                              <GoalIcon
                                id={id}
                                goalsStatusData={goalsStatusData}
                                sectionStatus={section_status}
                              />
                            }
                            title={
                              <span style={returnGoalStyle(id)}>
                                {is_goal
                                  ? `Goal ${order}: ${name}`
                                  : mil_no
                                  ? `MIL ${mil_no}: ${name}`
                                  : name}
                              </span>
                            }
                            description={
                              selectedGoalID === id && (
                                <Row gutter={16}>
                                  <QuestionBadges
                                    goal={goal}
                                    handleQuestionClick={handleQuestionClick}
                                  />
                                </Row>
                              )
                            }
                          />
                        );
                      })}
                  </Steps>
                ) : (
                  <Loader showSpin />
                ))}
            </Menu.Item>
          ))}
        </Menu>
      )}
    </div>
  );
}
